import { useMutation } from '@apollo/client'
import type { PromptResolve } from '@ur-solutions/react-components'
import { useToast } from '@ur-solutions/react-components'
import { useTranslate, useForm } from '@ur-solutions/react-hooks'
import { RegularModal, DatePicker, Checkbox } from '@/components_deprecated'
import React from 'react'
import styled from 'styled-components'
import type {
  ToggleUserActivatedMutation,
  ToggleUserActivatedMutationVariables,
  UserNode,
} from '../types.graphql'
import { TOGGLE_USER_ACTIVATED_MUTATION } from '../mutations'

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;

  & > label {
    font-size: 0.9rem;
    font-weight: 600;
    margin: 0 0 6px 6px;
    color: ${props => props.theme.colors.gray5};
  }
`

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  > span {
    width: 100%;
  }
`

interface UserActivationModalProps {
  user: UserNode
  onResolve: PromptResolve<boolean>
}

export const UserActivationModal: React.FC<UserActivationModalProps> = ({
  user,
  onResolve,
}) => {
  const isDeactivating = user.isActive

  const translations = useTranslate({
    title: isDeactivating ? 'users.deactivate-user' : 'users.activate-user',

    activate: 'users.activate-user',
    deactivate: 'users.deactivate-user',
    cancel: 'common.cancel',

    startDate: 'common.start-date',
    endDate: 'common.end-date',

    toastUserActivated: [
      'users.toasts.user-activated',
      { name: user.fullName },
    ],
    toastUserDeactivated: [
      'users.toasts.user-deactivated',
      { name: user.fullName },
    ],
    errorGeneric: 'error.generic-server-error',
    startEndDateError: 'timesheets.error.start-before-end',
  })

  const addToast = useToast()

  interface UserActivationForm {
    startDate: Date | null
    endDate: Date | null
    useEndDate: boolean
  }

  const {
    formValues: form,
    formValid,
    formErrors: errors,
    updateForm,
  } = useForm<UserActivationForm>({
    values: {
      startDate: null,
      endDate: null,
      useEndDate: false,
    },
    validators: {
      endDate: (val: Date | null) => {
        if (val && form.startDate && val <= form.startDate) {
          return translations.startEndDateError
        }
        return null
      },
    },
  })

  const [mutation, { loading }] = useMutation<
    ToggleUserActivatedMutation,
    ToggleUserActivatedMutationVariables
  >(TOGGLE_USER_ACTIVATED_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: ['Users'],
    onCompleted(data) {
      if (typeof data === 'undefined') {
        addToast('error', translations.errorGeneric)
        onResolve(false)
        return
      }

      const { user: updatedUser } = data.toggleUserActivated
      const message = updatedUser.isActive
        ? translations.toastUserActivated
        : translations.toastUserDeactivated

      addToast('success', message({ name: updatedUser.fullName }))
      onResolve(true)
    },
    onError(error) {
      addToast('error', translations.errorGeneric)
      console.error('Error toggling user activation:', error)
      onResolve(false)
    },
  })

  function handleSubmit() {
    const newActiveState = !user.isActive
    const { startDate, endDate } = form

    void mutation({
      variables: {
        userId: user.id,
        active: newActiveState,
        ...(startDate && { startDate: startDate.toISOString().split('T')[0] }),
        ...(endDate && { endDate: endDate.toISOString().split('T')[0] }),
      },
    })
  }

  return (
    <RegularModal
      title={translations.title}
      loading={loading}
      submitText={
        isDeactivating ? translations.deactivate : translations.activate
      }
      cancelText={translations.cancel}
      submitDisabled={!formValid}
      width="480px"
      onCancel={() => onResolve(false)}
      onSubmit={handleSubmit}
    >
      {isDeactivating && (
        <Form>
          <Field>
            <label>{translations.startDate}</label>
            <DatePicker
              value={form.startDate ?? new Date()}
              fullWidth
              clearable
              grayed
              onChange={value => updateForm({ startDate: value })}
            />
          </Field>

          <Field>
            <label>{translations.endDate}</label>
            <CheckboxWrapper>
              <Checkbox
                checked={form.useEndDate}
                onChange={val => {
                  const newValues = {
                    ...form,
                    useEndDate: val,
                    endDate: val ? form.endDate : null,
                  }
                  updateForm(newValues)
                }}
              />
              <DatePicker
                value={form.endDate ?? new Date()}
                fullWidth
                width="100%"
                clearable
                grayed
                disabled={!form.startDate && !form.useEndDate}
                onChange={value => updateForm({ endDate: value })}
                dateErrorText={errors.endDate ?? undefined}
              />
            </CheckboxWrapper>
          </Field>
        </Form>
      )}
    </RegularModal>
  )
}
